import { ChevronDownIcon, PlusCircleIcon } from '@heroicons/react/20/solid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Pipeline } from '@ui/__generated__/graphql'
import { Hierarchy1Icon } from '@ui/components/icons'
import { useClickAway } from '@uidotdev/usehooks'
import clsx from 'clsx'

export const PipelineDropdown = ({
  currentPipeline,
  pipelines,
  isOpen,
  openDropdown,
  handleSelectPipeline,
  updatePipelineName,
  newPipelineId,
}: {
  currentPipeline: Pipeline
  pipelines: Pipeline[]
  isOpen: boolean
  openDropdown: () => void
  handleSelectPipeline: (pipelineId: string) => void
  updatePipelineName: (name: string) => void
  newPipelineId: string
}) => {
  const inputRef = useRef<HTMLInputElement>(null)

  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState<string>('')

  const originalPipelineName = useMemo(() => currentPipeline.name, [currentPipeline])

  useEffect(() => {
    setInputValue(currentPipeline.name)
  }, [currentPipeline])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value)
      updatePipelineName(e.target.value)
    },
    [updatePipelineName]
  )

  const handleInputBlur = useCallback(() => {
    if (inputValue.length <= 0) {
      setInputValue(originalPipelineName)
    }
    setIsEditing(false)
  }, [inputValue, originalPipelineName])

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleInputBlur()
      }
    },
    [handleInputBlur]
  )

  // Click outside of the dropdown to close it
  const dropdownRef = useClickAway(() => {
    openDropdown()
  })

  // Click outside of the input to blur
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        handleInputBlur()
      }
    }

    window.addEventListener('mousedown', handleClickOutside)
    return () => {
      window.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleInputBlur])

  return (
    <div className="absolute left-0 top-0 flex h-full flex-row items-center">
      <div className="flex flex-row items-center space-x-2 whitespace-nowrap px-2 py-1">
        <Hierarchy1Icon className="h-4 w-4 shrink-0 stroke-white" />
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            autoFocus
            className={clsx(
              'w-auto rounded-md bg-white text-sm font-medium text-black transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-secondary-500',
              {
                'bg-gray-900': isEditing,
              }
            )}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
          />
        ) : (
          <div
            className={clsx(
              'rounded-md border-2 border-transparent p-2 px-1 text-sm font-medium text-white transition-all duration-300 hover:cursor-pointer hover:border-gray-900 hover:bg-gray-500'
            )}
            onClick={() => setIsEditing(true)}>
            {inputValue}
          </div>
        )}
        <div
          onClick={openDropdown}
          className={clsx(
            'cursor-pointer rounded-md border-2 border-transparent p-2 transition-all duration-300 hover:border-gray-900 hover:bg-gray-500',
            isOpen && 'border-gray-900 bg-gray-500'
          )}>
          <ChevronDownIcon
            className={clsx(
              'h-4 w-4 shrink-0 fill-white stroke-white transition-transform duration-300',
              isOpen && 'rotate-180'
            )}
          />
        </div>
      </div>
      {isOpen && (
        <div
          ref={dropdownRef as React.LegacyRef<HTMLDivElement>}
          className="fixed top-12 z-30 flex w-[14rem] -translate-x-4 flex-col rounded-md border border-gray-200 bg-white shadow-md">
          {pipelines.map((pipeline) => (
            <div
              key={pipeline.id}
              className={clsx(
                'flex w-full flex-row items-center truncate border-b border-gray-200 px-4 py-1 transition-all last:border-b-0 hover:bg-gray-200',
                {
                  'rounded-b-md bg-gray-100 hover:bg-gray-200': pipeline.id === newPipelineId,
                }
              )}
              onClick={() => handleSelectPipeline(pipeline.id)}>
              {pipeline.id === newPipelineId && <PlusCircleIcon className="h-5 w-5 text-primary-500" />}

              <p
                className={clsx('flex w-full cursor-pointer truncate px-1', {
                  'font-medium': pipeline.id === newPipelineId,
                })}>
                {pipeline.name}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
